import CollapseWithLinkButton from './components/CollapseWithLinkButton';
import { TranslationService } from '../../../../services/translation.service';
import Checkbox from './components/Checkbox';
import LocationAutocomplete from '../../../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../../../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../../../models/location-type.enum';
import ValidationInfo from '../../../../validation/validation-info';
import RadioButton from './components/RadioButton';
import { InterchangeDuration } from './models/interchange-duration.enum';
import exclamationCircleYellowIcon from '../../../../../assets/icons/exclamation-circle-filled-orange-400.svg';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import TravelByFootOrBike from './components/TravelByFootOrBike';
import { TransportMode } from '../../../../models/transport-mode.enum';
import TransportModeFilter from '../../../Shared/TransportModeFilter';
import { TransportSubMode } from '../../../../models/transport-sub-mode.enum';
import ClearButton from '../../../Shared/ClearButton';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { filtersAreEqual, initialTravelPlannerFilters, type ITravelPlannerFilters } from './models/travel-planner-filters.model';
import { FilterHelper } from './helpers/filter.helper';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../../services/client-side-storage.service';
import { ClientSideStorageType } from '../../../../models/client-side-storage-type.enum';
import { useEffect, useState } from 'react';
import { AnalyticsService } from '../../../../services/analytics-service';
import { TransportSubModeHelper } from '../../../../helpers/transport-sub-mode.helper';
import { TransportModeHelper } from '../../../../helpers/transport-mode.helper';
import { t } from '../../../../services/texts.service';

function createCollapseLinkText(numberOfFiltersSelected: number): string {
  if (numberOfFiltersSelected === 0) {
    return TranslationService.get('travelplanner.moreoptions');
  }
  if (numberOfFiltersSelected === 1) {
    return `${TranslationService.get('travelplanner.moreoptions')} (1 ${TranslationService.get('travelplanner.changedfilter').toLocaleLowerCase()})`;
  }
  return `${TranslationService.get('travelplanner.moreoptions')} (${numberOfFiltersSelected} ${TranslationService.get('travelplanner.changedfilters').toLocaleLowerCase()})`;
}

function TravelPlannerFilters({
  mostRecentlyUsed,
  state,
  onChange,
  onSaveSelectionsChange,
  onClear,
}: {
  mostRecentlyUsed: ITravelPlannerFilters;
  state: ITravelPlannerFilters;
  onChange: (state: ITravelPlannerFilters) => void;
  onSaveSelectionsChange: (value: boolean) => void;
  onClear?: () => void;
}) {
  // === CONFIGURATION ===
  const [functionalConsentGiven, setFunctionalConsentGiven] = useState(ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional));
  const [travelPlannerFiltersSaveChange, setTravelPlannerFiltersSaveChange] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // === VARIABLES ===
  const availableTransportModes = [TransportMode.tram, TransportMode.bus, TransportMode.ferry];
  const isDirty = !filtersAreEqual(state, mostRecentlyUsed);
  const numberOfChangedFilters = FilterHelper.numberOfFiltersChanged(state, initialTravelPlannerFilters);
  const isNoModeSelected = state.transportModes.length === 0 && !state.showWalking && !state.showBiking;
  const saveSelections = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave) !== '0';

  useEffect(() => {
    if (!ClientSideStorageService.isConsentStatusSet()) {
      ClientSideStorageService.onConsentStatusChange()
        .then(() => {
          const hasConsentChanged = ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional);
          if (hasConsentChanged) {
            setFunctionalConsentGiven(hasConsentChanged);
          }
        })
        .catch(() => {});
    }
  }, []);

  // === HELPER FUNCTIONS ===
  function findChangedMode(prevModes: TransportMode[], nextModes: TransportMode[], prevSubModes: TransportSubMode[], nextSubModes: TransportSubMode[]): TransportMode | TransportSubMode | undefined {
    return (
      nextSubModes.find((m) => !prevSubModes.includes(m)) ??
      prevSubModes.find((m) => !nextSubModes.includes(m)) ??
      nextModes.find((m) => !prevModes.includes(m)) ??
      prevModes.find((m) => !nextModes.includes(m))
    );
  }

  return (
    <div>
      <CollapseWithLinkButton
        ariaLive="polite"
        linkText={createCollapseLinkText(numberOfChangedFilters)}
        onToggle={() => {
          AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Öppna filtermeny');
          setIsCollapsed(!isCollapsed);
        }}
      >
        <h2 className="visually-hidden">{TranslationService.get('travelplanner.moreoptions')}</h2>
        <div className="travel-planner-filters">
          <div className="mb-3">
            <h3 className="h4">{TranslationService.get('travelplanner.route')}</h3>
            <Checkbox
              id="no-transfers"
              label={TranslationService.get('travelplanner.notransfers')}
              checked={state.onlyDirectConnections}
              onChange={(value) => {
                onChange({ ...state, onlyDirectConnections: value });
                AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Visa endast resor utan byten', value ? 'Ja' : 'Nej');
              }}
            />
          </div>
          <div className="mb-3 border-bottom pb-3">
            <LocationAutocomplete
              id="via"
              labelClassName="h4"
              locationTypes={[LocationType.stopArea]}
              labelText="Via"
              placeholderText={TranslationService.get('common.stop')}
              state={{ value: state.viaStopArea ? { type: LocationAutocompleteSelectionType.location, location: state.viaStopArea } : undefined, validationInfo: new ValidationInfo() }}
              onSelect={(value) => {
                onChange({ ...state, viaStopArea: value?.location });
              }}
            />
          </div>
          {!state.onlyDirectConnections && (
            <fieldset id="interchange-duration" className="mb-3 border-bottom pb-3">
              <legend className="h4">{TranslationService.get('travelplanner.interchangeduration')}</legend>
              <div className="d-flex flex-wrap flex-column gap-2">
                <RadioButton
                  id="interchange-duration-short"
                  name="interchange-duration"
                  labelText={TranslationService.get('travelplanner.interchangedurationshort')}
                  value={InterchangeDuration.short}
                  checked={state.interchangeDuration === InterchangeDuration.short}
                  helpIconSrc={DataUrlHelper.encode(exclamationCircleYellowIcon)}
                  helpText={TranslationService.get('travelplanner.interchangedurationwarning')}
                  className="mb-0"
                  onChange={(value) => {
                    onChange({ ...state, interchangeDuration: value as InterchangeDuration });
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Minsta bytestid', 'Kort (minst 2 min)');
                  }}
                />
                <RadioButton
                  id="interchange-duration-normal"
                  name="interchange-duration"
                  labelText={TranslationService.get('travelplanner.interchangedurationnormal')}
                  value={InterchangeDuration.normal}
                  checked={state.interchangeDuration === InterchangeDuration.normal}
                  className="mb-0"
                  onChange={(value) => {
                    onChange({ ...state, interchangeDuration: value as InterchangeDuration });
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Minsta bytestid', 'Normal (oftast 5 min)');
                  }}
                />
              </div>
            </fieldset>
          )}
          <div className="mb-3 border-bottom pb-3">
            <h3 className="h4">{TranslationService.get('travelplanner.nearbystopareas')}</h3>
            <Checkbox
              id="nearby-stopareas"
              className="mb-0"
              label={TranslationService.get('travelplanner.transfernearbystopareas')}
              checked={state.includeNearbyStopAreas}
              onChange={(value) => {
                onChange({ ...state, includeNearbyStopAreas: value });
                AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Närliggande hållplatser', value ? 'Ja' : 'Nej');
              }}
            />
          </div>
          <fieldset className="mb-3 border-bottom pb-3" aria-describedby={isNoModeSelected ? 'transport-mode-error' : undefined}>
            <legend className="h4">{TranslationService.get('travelplanner.alternativejourneys')}</legend>
            <TravelByFootOrBike
              className="mt-1"
              isInvalid={isNoModeSelected}
              state={{
                showWalking: state.showWalking,
                showBiking: state.showBiking,
                bikeMaxDistance: state.bikingMaxDistance,
                bikeSpeed: state.bikingSpeed,
              }}
              onChange={(value) => {
                onChange({
                  ...state,
                  showWalking: value.showWalking,
                  showBiking: value.showBiking,
                  bikingMaxDistance: value.bikeMaxDistance,
                  bikingSpeed: value.bikeSpeed,
                });
                AnalyticsService.addEvent(
                  AnalyticsService.TravelPlannerCategoryFilters,
                  'Alternativa resesätt',
                  value.showWalking && value.showBiking ? 'Visa både gång och cykel' : value.showWalking ? 'Visa gång' : value.showBiking ? 'Visa cykel' : 'Visa inga',
                );
              }}
            />
          </fieldset>
          <fieldset className="mb-3 border-bottom pb-3" aria-describedby={isNoModeSelected ? 'transport-mode-error' : undefined}>
            <legend className="h4">{TranslationService.get('travelplanner.transportmode')}</legend>
            <TransportModeFilter
              availableTransportModes={availableTransportModes}
              availableTransportSubModes={[TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain]}
              groupNonVasttagenAsOtherTrains={true}
              selectedTransportModes={state.transportModes}
              selectedTransportSubModes={state.transportSubModes}
              isInvalid={isNoModeSelected}
              onChange={(transportModes, transportSubModes) => {
                onChange({ ...state, transportModes, transportSubModes });
                // Analytics for transport mode changes
                const changedMode = findChangedMode(state.transportModes, transportModes, state.transportSubModes, transportSubModes);
                if (changedMode !== undefined) {
                  const isChecked = transportModes.includes(changedMode as TransportMode) || transportSubModes.includes(changedMode as TransportSubMode);
                  const modeName = Object.values(TransportMode).includes(changedMode as TransportMode) ? TransportModeHelper.translate(changedMode) : TransportSubModeHelper.translate(changedMode);
                  AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, `Trafikslag ${modeName}`, isChecked ? 'Ja' : 'Nej');
                }
              }}
            />
            {isNoModeSelected && (
              <div className="invalid-feedback d-block mt-2" id="transport-mode-error" role="alert">
                <div>{t('Välj minst ett trafikslag eller resesätt', 'Select at least one transport mode or travel option')}</div>
              </div>
            )}
          </fieldset>
          <fieldset className="mb-3 border-bottom pb-3">
            <legend className="h4">{TranslationService.get('travelplanner.settings')}</legend>
            <div className="d-flex flex-wrap flex-column gap-2">
              <Checkbox
                id="show-occupancy"
                className="mb-0"
                label={TranslationService.get('travelplanner.useoccupancy')}
                checked={state.showOccupancy}
                onChange={(value) => {
                  onChange({ ...state, showOccupancy: value });
                  AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Prognos för trängsel', value ? 'Ja' : 'Nej');
                }}
              />
              <Checkbox
                id="save-selections"
                className="mb-0"
                label={TranslationService.get('travelplanner.saveselections')}
                checked={!!saveSelections && !!functionalConsentGiven}
                disabled={!functionalConsentGiven}
                helpText={
                  !functionalConsentGiven ? (
                    <>
                      {TranslationService.get('travelplanner.saveselectionscookie')}
                      <a href={TranslationService.get('common.cookiepageurl')}>{TranslationService.get('common.managecookies')}</a>
                    </>
                  ) : undefined
                }
                interactiveHelpText={true}
                onChange={(value) => {
                  ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave, value ? '1' : '0');
                  setTravelPlannerFiltersSaveChange(!travelPlannerFiltersSaveChange);
                  onSaveSelectionsChange(value);
                  AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategoryFilters, 'Spara mina val', value ? 'Ja' : 'Nej');
                }}
              />
            </div>
          </fieldset>
          <div className="mb-3">
            <ClearButton
              onClick={() => {
                onChange(initialTravelPlannerFilters);
                onClear?.();
              }}
            />
          </div>
        </div>
      </CollapseWithLinkButton>
      <div aria-live="polite">
        {isDirty && (
          <div
            className="filter-help-text bg-gray p-3 mb-3 text-center last-child-margin-bottom-none"
            dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('travelplanner.filterchangedhelptext')) }}
          />
        )}
        {isNoModeSelected && isCollapsed && (
          <div className="alert general-error mb-3 text-center" role="alert">
            {t('Välj minst ett trafikslag eller resesätt i sökalternativen', 'Select at least one transport mode or travel option in the search options')}
          </div>
        )}
      </div>
    </div>
  );
}

export default TravelPlannerFilters;
